import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Search } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import {
  GET_LEAVE_REQUESTS_APPROVE_HR,
  LEAVE_APPROVE_MANAGER,
} from "../../../../../../config/Endpoints.js";
import { Event } from "../../../../../../config/events.js";
import { dispatchEvent } from "../../../../../../Hooks/useEvent.js";
import { getUser } from "../../../../../../utils/Storage.js";
import {
  displayFormatDate,
  formatLeaveType,
} from "../../../../../../utils/helpers.js";
import {
  Badge,
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  TextArea,
  TextField,
  type BadgeVariant,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import { getApi, postApi } from "../../../../../utils/api.ts";
import { accessToken } from "../../../../../utils/auth.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { LEAVE_STATUS_OPTIONS } from "../../constants.ts";
import type { LeaveApprovalRow } from "../../types.ts";
import { extractMessage } from "../../hooks.ts";
import styles from "./leave-approval-tab.module.css";

const STATUS_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  PENDING: { label: "Pending", variant: "warning" },
  APPROVED: { label: "Approved", variant: "success" },
  REJECTED: { label: "Rejected", variant: "danger" },
  WITHDRAW: { label: "Withdrawn", variant: "neutral" },
  WITHDRAWN: { label: "Withdrawn", variant: "neutral" },
};

function readUserId(): number | null {
  try {
    const raw = getUser("userData");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function unwrapLeaveContent(payload: unknown): {
  content: LeaveApprovalRow[];
  totalPages: number;
} {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;
  const data =
    root && typeof root === "object" && "data" in root
      ? (root as { data?: unknown }).data
      : root;
  const record = data && typeof data === "object" ? data as Record<string, unknown> : {};
  const content = Array.isArray(record.content) ? record.content : [];
  return {
    content: content.map((item, index) => {
      const row = item as LeaveApprovalRow;
      return {
        ...row,
        id: String(row.requestId ?? index),
      };
    }),
    totalPages: Number(record.totalPages ?? 1) || 1,
  };
}

function statusBadge(status?: string) {
  const normalized = String(status ?? "").toUpperCase();
  const meta = STATUS_MAP[normalized] ?? { label: status || "-", variant: "neutral" as BadgeVariant };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export function LeaveApprovalTab() {
  const userId = useMemo(() => readUserId(), []);
  const [rows, setRows] = useState<LeaveApprovalRow[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("PENDING");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<LeaveApprovalRow | null>(null);
  const [leaveAction, setLeaveAction] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | undefined>();

  const fetchList = useCallback(async () => {
    if (userId == null) {
      setRows([]);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    try {
      const statusQuery =
        selectedStatus === "MANAGER_PENDING"
          ? "&managerStatus=PENDING"
          : selectedStatus
            ? `&status=${selectedStatus}`
            : "";
      const { data } = await getApi<unknown>(
        `${GET_LEAVE_REQUESTS_APPROVE_HR}/${userId}/pending?size=${pageSize}&page=${page - 1}${statusQuery}`,
        accessToken(),
      );
      const parsed = unwrapLeaveContent(data);
      setRows(parsed.content);
      setTotalPages(parsed.totalPages);
      setSearchTerm("");
    } catch (e) {
      showError(extractMessage(e, "Failed to load leave requests."));
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, selectedStatus, userId]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      row.employeeName?.toLowerCase().includes(q),
    );
  }, [rows, searchTerm]);

  const resetModal = () => {
    setSelectedLeave(null);
    setLeaveAction("");
    setReason("");
    setFormError(undefined);
  };

  const submitAction = async () => {
    if (!selectedLeave || userId == null) return;
    if (!leaveAction) {
      setFormError("Action is required.");
      return;
    }
    if (reason.trim().length < 5) {
      setFormError("Reason must be at least 5 characters long.");
      return;
    }

    setSubmitting(true);
    try {
      const isApprove = leaveAction === "APPROVED";
      const url = isApprove
        ? `${GET_LEAVE_REQUESTS_APPROVE_HR}/${selectedLeave.requestId}/approve`
        : `${LEAVE_APPROVE_MANAGER}/${selectedLeave.requestId}/reject`;
      const payload = isApprove
        ? { managerId: userId, comment: reason.trim() }
        : { managerId: userId, reason: reason.trim() };
      const { data } = await postApi<{ message?: string }>(
        url,
        accessToken(),
        payload,
      );
      showSuccess(data?.message || "Leave updated successfully.");
      dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
      resetModal();
      await fetchList();
    } catch (e) {
      showError(extractMessage(e, "Failed to update leave request."));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo<ResponsiveColumn<LeaveApprovalRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
        truncate: true,
      },
      {
        id: "fromDate",
        header: "From",
        sortable: true,
        nowrap: true,
        render: (row) => displayFormatDate(row.fromDate ?? ""),
        getSortValue: (row) => row.fromDate ?? "",
      },
      {
        id: "toDate",
        header: "To",
        sortable: true,
        nowrap: true,
        render: (row) => displayFormatDate(row.toDate ?? ""),
        getSortValue: (row) => row.toDate ?? "",
      },
      {
        id: "managerName",
        header: "Approved By",
        accessor: "managerName",
        sortable: true,
        truncate: true,
      },
      {
        id: "leaveType",
        header: "Leave Type",
        sortable: true,
        render: (row) => formatLeaveType(row.leaveType ?? "-"),
      },
      {
        id: "totalDays",
        header: "Leave Count",
        accessor: "totalDays",
        sortable: true,
        nowrap: true,
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        truncate: true,
        getTitleText: (row) => row.reason ?? "-",
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        render: (row) => statusBadge(row.status),
        getSortValue: (row) => row.status ?? "",
      },
      ...(selectedStatus === "PENDING"
        ? [
            {
              id: "action",
              header: "Action",
              sortable: false,
              nowrap: true,
              render: (row: LeaveApprovalRow) =>
                row.status === "PENDING" ? (
                  <Button
                    size="sm"
                    variant="muted-outline"
                    startIcon={<Eye size={14} aria-hidden />}
                    onClick={() => {
                      setSelectedLeave(row);
                      setLeaveAction("");
                      setReason("");
                      setFormError(undefined);
                    }}
                  >
                    View
                  </Button>
                ) : (
                  "-"
                ),
            } satisfies ResponsiveColumn<LeaveApprovalRow>,
          ]
        : []),
    ],
    [selectedStatus],
  );

  const modalRows = useMemo(
    () => (selectedLeave ? [selectedLeave] : []),
    [selectedLeave],
  );

  const modalColumns = useMemo<ResponsiveColumn<LeaveApprovalRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        truncate: true,
      },
      {
        id: "fromDate",
        header: "From",
        nowrap: true,
        render: (row) => displayFormatDate(row.fromDate ?? ""),
      },
      {
        id: "toDate",
        header: "To",
        nowrap: true,
        render: (row) => displayFormatDate(row.toDate ?? ""),
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        truncate: true,
        getTitleText: (row) => row.reason ?? "-",
      },
      {
        id: "leaveType",
        header: "Leave Type",
        render: (row) => formatLeaveType(row.leaveType ?? "-"),
      },
      {
        id: "totalDays",
        header: "Leave Count",
        accessor: "totalDays",
        nowrap: true,
      },
    ],
    [],
  );

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>
      
        <div className={styles.filters}>
          <TextField
            id="leave-approval-search"
            label="Employee Name"
            placeholder="Search by Employee Name"
            value={searchTerm}
            startIcon={<Search size={16} aria-hidden />}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Select
            id="leave-approval-status"
            label="Status"
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value || "PENDING");
              setPage(1);
            }}
            options={LEAVE_STATUS_OPTIONS}
            placeholder="Filter By Status"
          />
        </div>
      </section>

      <section>
        {loading ? (
          <div className={styles.loadingWrap}>
            <ThreeDots
              height="56"
              width="56"
              color="var(--color-accent-500)"
              visible
            />
          </div>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            data={filteredRows}
            aria-label="Final leave approvals"
            emptyState={<EmptyStateMessage message="No record available." />}
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={totalPages * pageSize}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            totalLabel={
              <>
                Total Leaves: <strong>{filteredRows.length}</strong>
              </>
            }
          />
        )}
      </section>

      <Modal
        open={selectedLeave != null}
        onClose={resetModal}
        title="Approve Leave Action"
        size="lg"
        footer={
          <>
          <div className={styles.modalFooter}>
              <Button variant="light-grey" onClick={resetModal} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submitAction} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </Button>
            </div>
          </>
        }
      >
        <ResponsiveDataTable
          columns={modalColumns}
          data={modalRows}
          aria-label="Selected leave request"
          pagination={false}
        />
        <div className={styles.modalControls}>
          <Select
            id="leave-action"
            label="Action"
            value={leaveAction}
            onChange={(value) => {
              setLeaveAction(value);
              setFormError(undefined);
            }}
            options={[
              { value: "APPROVED", label: "Approve" },
              { value: "REJECTED", label: "Reject" },
            ]}
            placeholder="Select Action"
            required
          />
          <TextArea
            id="leave-action-reason"
            label="Reason"
            value={reason}
            rows={3}
            required
            error={formError}
            onChange={(event) => {
              setReason(event.target.value);
              setFormError(undefined);
            }}
          />
        </div>
      </Modal>
    </div>
  );
}

